import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Eye, EyeOff, ArrowRight, Check, ShieldCheck, AlertCircle, RefreshCw } from 'lucide-react';
import { User } from '../types';

interface AuthProps {
  onNavigate: (page: string) => void;
  onLogin: (user: User) => void;
  initialAdmin?: boolean;
  initialMode?: 'login' | 'register';  // ADDED: Support for initial mode
}

const backgroundSlides = [
  "https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?q=80&w=2070&auto=format&fit=crop", 
  "https://images.unsplash.com/photo-1605100804763-247f67b3557e?q=80&w=2070&auto=format&fit=crop", 
  "https://images.unsplash.com/photo-1602173574767-37ac01994b2a?q=80&w=2070&auto=format&fit=crop", 
  "https://images.unsplash.com/photo-1523170335258-f5ed11844a49?q=80&w=2079&auto=format&fit=crop"  
];

const Auth: React.FC<AuthProps> = ({ 
  onNavigate, 
  onLogin, 
  initialAdmin = false,
  initialMode = 'login'  // ADDED: Default to login
}) => {
  const [isLogin, setIsLogin] = useState(initialMode === 'login');  // FIXED: Use initialMode
  const [email, setEmail] = useState("");
  const [isAdmin, setIsAdmin] = useState(initialAdmin);
  const [showPassword, setShowPassword] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  // Forgot Password State
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  // Secret Click Logic
  const clickTimeoutRef = useRef<any>(null);
  const clickCountRef = useRef(0);

  // ADDED: Check URL parameters for mode
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const mode = params.get('mode');
    if (mode === 'register') {
      setIsLogin(false);
    } else if (mode === 'login') {
      setIsLogin(true);
    }
  }, []);

  useEffect(() => {
    setIsAdmin(initialAdmin);
  }, [initialAdmin]);

  // Slideshow Timer
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % backgroundSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setIsSuccess(false);
    setError('');
    setFormData({ name: '', email: '', password: '', confirmPassword: '' });
    // Update URL without reload
    const newUrl = !isLogin ? '/auth' : '/register';
    window.history.pushState(null, '', newUrl);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError('');
  };

  const handleSecretClick = (e: React.MouseEvent) => {
    e.preventDefault(); 
    
    if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
    }

    clickCountRef.current += 1;

    if (clickCountRef.current >= 5) {
        onNavigate('admin-portal');
        clickCountRef.current = 0;
    } else {
        clickTimeoutRef.current = setTimeout(() => {
            clickCountRef.current = 0;
        }, 500);
    }
  };

  const API_BASE_URL = '/api';

  // Handle Forgot Password
  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsResetting(true);
    setResetMessage('');
    
    try {
      const response = await fetch(`${API_BASE_URL}/auth/forgot-password.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setResetSuccess(true);
        setResetMessage('Password reset link sent to your email!');
        setTimeout(() => {
          setShowForgotPassword(false);
          setResetSuccess(false);
          setResetEmail('');
          setResetMessage('');
        }, 3000);
      } else {
        setResetMessage(data.message || 'Email not found');
      }
    } catch (error) {
      setResetMessage('Connection error. Please try again.');
    } finally {
      setIsResetting(false);
    }
  };

  // Handle Registration
  const handleRegister = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/register.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: formData.name,
          email: formData.email,
          password: formData.password
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setIsSuccess(true);
        setTimeout(() => {
          setIsLogin(true);
          setIsSuccess(false);
          setFormData({ name: '', email: '', password: '', confirmPassword: '' });
          // Update URL to auth after registration
          window.history.pushState(null, '', '/auth');
        }, 2000);
      } else {
        setError(data.message || 'Registration failed');
      }
    } catch (error) {
      setError('Connection error. Please try again.');
      console.error('Registration error:', error);
    }
  };

  // Handle Login
  const handleLogin = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setIsSuccess(true);
        
        // Ensure user object has all required fields including avatar
        const userData: User = {
          id: data.user.id,
          email: data.user.email,
          name: data.user.name || data.user.username || 'Member',
          role: data.user.role || 'member',
          avatar: data.user.avatar || '',
          phone: data.user.phone,
          created_at: data.user.created_at
        };
        
        // Save user to localStorage
        localStorage.setItem('sparklingUser', JSON.stringify(userData));
        
        setTimeout(() => {
          onLogin(userData);
        }, 1500);
      } else {
        setError(data.message || 'Invalid email or password');
      }
    } catch (error) {
      setError('Connection error. Please try again.');
      console.error('Login error:', error);
    }
  };

  // Handle Admin Login
  const handleAdminLogin = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password
        })
      });
      
      const data = await response.json();
      
      if (data.success && data.user.role === 'admin') {
        setIsSuccess(true);
        
        const userData: User = {
          id: data.user.id,
          email: data.user.email,
          name: data.user.name || 'Administrator',
          role: 'admin',
          avatar: data.user.avatar || '',
          phone: data.user.phone,
          created_at: data.user.created_at
        };
        
        localStorage.setItem('sparklingUser', JSON.stringify(userData));
        
        setTimeout(() => {
          onLogin(userData);
        }, 1500);
      } else if (data.success && data.user.role !== 'admin') {
        setError('Access denied. Admin privileges required.');
      } else {
        setError(data.message || 'Invalid credentials');
      }
    } catch (error) {
      setError('Connection error. Please try again.');
      console.error('Admin login error:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    // Validation
    if (!isLogin && formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    if (!isLogin && formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      setIsLoading(false);
      return;
    }
    
    // Admin Authentication
    if (isAdmin) {
      await handleAdminLogin();
      setIsLoading(false);
      return;
    }
    
    // Standard User
    if (isLogin) {
      await handleLogin();
    } else {
      await handleRegister();
    }
    
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen flex bg-stone-50 text-stone-900">
      
      {/* Left Panel - Image Slideshow */}
      <div className="hidden lg:block lg:w-1/2 relative overflow-hidden bg-stone-900">
        {backgroundSlides.map((slide, index) => (
          <img 
            key={index}
            src={slide}
            alt="Luxury Jewelry" 
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[2000ms] ease-in-out ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            } animate-[zoomSlow_25s_infinite_alternate]`}
          />
        ))}

        <div className="absolute inset-0 bg-stone-900/30 z-10"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-stone-900/90 via-transparent to-transparent z-20 flex flex-col justify-end p-16 text-white">
            <h2 className="font-serif text-4xl mb-4 leading-tight drop-shadow-lg transition-all duration-500" key={isLogin ? 'login' : 'register'}>
                {isAdmin 
                  ? "Maison Management" 
                  : (isLogin ? "Welcome Back to the Vault" : "Join the Circle of Brilliance")
                }
            </h2>
            <p className="font-light text-stone-200 max-w-md text-sm leading-relaxed border-l border-gold-500 pl-6 drop-shadow-md">
                {isAdmin
                  ? "Secure access for inventory management, client concierge services, and appointment scheduling."
                  : (isLogin 
                      ? "Access your curated wishlist, order history, and exclusive member-only viewings." 
                      : "Create an account to unlock bespoke services, early access to new collections, and personalized consultations."
                    )
                }
            </p>
            
            {/* Slide Indicators */}
            <div className="flex gap-2 mt-8">
              {backgroundSlides.map((_, idx) => (
                <div 
                  key={idx} 
                  className={`h-1 rounded-full transition-all duration-500 ${
                    idx === currentSlide ? 'w-8 bg-gold-500' : 'w-2 bg-white/30'
                  }`}
                />
              ))}
            </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center items-center p-6 md:p-12 relative bg-white">
        
        {/* Back Button */}
        <button 
          onClick={() => onNavigate('home')}
          className="absolute top-8 left-8 flex items-center gap-2 text-stone-400 hover:text-stone-900 transition-colors text-xs uppercase tracking-widest"
        >
          <ArrowLeft size={14} /> Back to Maison
        </button>

        <div className="w-full max-w-md">
            {/* Header */}
            <div className="text-center mb-10">
                <div className="flex justify-center mb-4">
                    {isAdmin && (
                        <div className="bg-stone-100 p-3 rounded-full mb-2 animate-fade-in">
                             <ShieldCheck size={24} className="text-stone-700" strokeWidth={1.5} />
                        </div>
                    )}
                </div>
                <span className={`text-[10px] tracking-[0.3em] uppercase font-bold mb-4 block ${isAdmin ? 'text-stone-500' : 'text-gold-600'}`}>
                    {isAdmin ? "Staff Portal" : (isLogin ? "Member Access" : "Registration")}
                </span>
                <h1 className="font-serif text-4xl text-stone-900 mb-2">
                    {isAdmin ? "Internal Access" : "Sparkling Gems"}
                </h1>
                <p 
                    className="text-stone-400 text-sm font-light italic cursor-default select-none"
                    onClick={handleSecretClick}
                    title={!isAdmin ? "Est. 1985" : undefined}
                >
                    {isAdmin ? "Authorized Personnel Only" : "Est. 1985"}
                </p>
            </div>

            {isSuccess ? (
                <div className="bg-stone-50 border border-stone-100 p-8 text-center animate-fade-in">
                    <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4 text-green-600">
                        <Check size={32} />
                    </div>
                    <h3 className="font-serif text-2xl mb-2">
                        {isAdmin ? "Access Granted" : (isLogin ? "Welcome Back" : "Account Created")}
                    </h3>
                    <p className="text-stone-500 text-sm mb-6">
                        {isAdmin 
                            ? "Redirecting to management dashboard..." 
                            : (isLogin ? "You have successfully logged in." : "Your account has been created. Please log in.")
                        }
                    </p>
                    <p className="text-xs text-stone-400">Redirecting...</p>
                </div>
            ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Error Message */}
                    {error && (
                        <div className="bg-red-50 border border-red-100 p-3 rounded flex items-center gap-3 text-red-600 animate-fade-in">
                            <AlertCircle size={16} />
                            <span className="text-xs">{error}</span>
                        </div>
                    )}

                    {/* Name field only for non-admin registration */}
                    {!isLogin && !isAdmin && (
                        <div className="group relative">
                            <input 
                                type="text" 
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                className="w-full bg-transparent border-b border-stone-300 py-3 text-stone-900 focus:outline-none focus:border-gold-600 transition-colors peer placeholder-transparent"
                                placeholder="Full Name"
                                id="auth-name"
                            />
                            <label htmlFor="auth-name" className="absolute left-0 -top-3.5 text-xs text-gold-600 transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-stone-400 peer-placeholder-shown:top-3 peer-focus:-top-3.5 peer-focus:text-gold-600 peer-focus:text-xs uppercase tracking-wider">
                                Full Name
                            </label>
                        </div>
                    )}

                    <div className="group relative">
                        <input 
                            type={isAdmin ? "text" : "email"}
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className={`w-full bg-transparent border-b py-3 text-stone-900 focus:outline-none transition-colors peer placeholder-transparent ${isAdmin ? 'border-stone-300 focus:border-stone-800' : 'border-stone-300 focus:border-gold-600'}`}
                            placeholder={isAdmin ? "Username" : "Email Address"}
                            id="auth-email"
                        />
                        <label htmlFor="auth-email" className={`absolute left-0 -top-3.5 text-xs transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-stone-400 peer-placeholder-shown:top-3 peer-focus:-top-3.5 peer-focus:text-xs uppercase tracking-wider ${isAdmin ? 'text-stone-800 peer-focus:text-stone-800' : 'text-gold-600 peer-focus:text-gold-600'}`}>
                            {isAdmin ? 'Username' : 'Email Address'}
                        </label>
                    </div>

                    <div className="group relative">
                        <input 
                            type={showPassword ? "text" : "password"} 
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            required
                            className={`w-full bg-transparent border-b py-3 text-stone-900 focus:outline-none transition-colors peer placeholder-transparent pr-8 ${isAdmin ? 'border-stone-300 focus:border-stone-800' : 'border-stone-300 focus:border-gold-600'}`}
                            placeholder="Password"
                            id="auth-password"
                        />
                        <label htmlFor="auth-password" className={`absolute left-0 -top-3.5 text-xs transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-stone-400 peer-placeholder-shown:top-3 peer-focus:-top-3.5 peer-focus:text-xs uppercase tracking-wider ${isAdmin ? 'text-stone-800 peer-focus:text-stone-800' : 'text-gold-600 peer-focus:text-gold-600'}`}>
                            Password
                        </label>
                        <button 
                            type="button" 
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-0 top-3 text-stone-400 hover:text-stone-600"
                        >
                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                    </div>

                    {!isLogin && !isAdmin && (
                        <div className="group relative">
                            <input 
                                type={showPassword ? "text" : "password"} 
                                name="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                required
                                className="w-full bg-transparent border-b border-stone-300 py-3 text-stone-900 focus:outline-none focus:border-gold-600 transition-colors peer placeholder-transparent"
                                placeholder="Confirm Password"
                                id="auth-confirm-password"
                            />
                            <label htmlFor="auth-confirm-password" className="absolute left-0 -top-3.5 text-xs text-gold-600 transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-stone-400 peer-placeholder-shown:top-3 peer-focus:-top-3.5 peer-focus:text-gold-600 peer-focus:text-xs uppercase tracking-wider">
                                Confirm Password
                            </label>
                        </div>
                    )}

                    {isLogin && (
                        <div className="flex justify-end">
                            <button 
                              type="button" 
                              onClick={() => setShowForgotPassword(true)}
                              className={`text-xs hover:underline transition-colors ${isAdmin ? 'text-stone-500 hover:text-stone-800' : 'text-stone-400 hover:text-gold-600'}`}
                            >
                                Forgot Password?
                            </button>
                        </div>
                    )}

                    <button 
                        type="submit" 
                        disabled={isLoading}
                        className={`w-full text-white py-4 px-8 uppercase tracking-[0.2em] text-xs transition-colors duration-300 flex items-center justify-center gap-2 shadow-lg disabled:opacity-70 disabled:cursor-not-allowed group mt-8 ${isAdmin ? 'bg-stone-800 hover:bg-stone-700' : 'bg-stone-900 hover:bg-gold-600'}`}
                    >
                        {isLoading ? (
                            <span className="animate-pulse">Processing...</span>
                        ) : (
                            <>
                                {isAdmin ? 'Authenticate' : (isLogin ? 'Sign In' : 'Create Account')}
                                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                            </>
                        )}
                    </button>
                </form>
            )}

            {/* Toggle Modes */}
            <div className="mt-12 text-center border-t border-stone-100 pt-8">
                {!isAdmin ? (
                    <>
                        <p className="text-stone-500 text-sm font-light">
                            {isLogin ? "Not a member yet?" : "Already have an account?"}
                        </p>
                        <button 
                            onClick={toggleMode}
                            className="mt-2 text-stone-900 uppercase tracking-widest text-xs font-bold hover:text-gold-600 transition-colors"
                        >
                            {isLogin ? "Create Account" : "Sign In"}
                        </button>
                    </>
                ) : (
                    <button 
                        onClick={() => onNavigate('auth')}
                        className="text-stone-500 uppercase tracking-widest text-[10px] font-bold hover:text-stone-900 transition-colors flex items-center justify-center gap-2 mx-auto"
                    >
                        <ArrowLeft size={12} /> Return to Member Login
                    </button>
                )}
            </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center">
          <div className="absolute inset-0 bg-stone-900/70 backdrop-blur-sm" onClick={() => setShowForgotPassword(false)}></div>
          <div className="relative bg-white w-full max-w-md rounded-sm shadow-2xl animate-fade-up-enter fade-up-active mx-4 overflow-hidden">
            <div className="bg-stone-900 text-white p-6">
              <h3 className="font-serif text-xl">Reset Password</h3>
              <p className="text-xs text-stone-400 mt-1">Enter your email to receive reset instructions</p>
            </div>
            
            <form onSubmit={handleForgotPassword} className="p-8 space-y-6">
              {resetMessage && (
                <div className={`p-3 rounded flex items-center gap-3 text-sm ${
                  resetSuccess ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'
                }`}>
                  {resetSuccess ? <Check size={16} /> : <AlertCircle size={16} />}
                  <span>{resetMessage}</span>
                </div>
              )}
              
              <div className="group relative">
                <input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                  className="w-full bg-transparent border-b border-stone-300 py-3 text-stone-900 focus:outline-none focus:border-gold-600 transition-colors peer placeholder-transparent"
                  placeholder="Email Address"
                  id="reset-email"
                />
                <label htmlFor="reset-email" className="absolute left-0 -top-3.5 text-xs text-gold-600 transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-stone-400 peer-placeholder-shown:top-3 peer-focus:-top-3.5 peer-focus:text-gold-600 peer-focus:text-xs">
                  Email Address
                </label>
              </div>
              
              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(false)}
                  className="flex-1 border border-stone-300 text-stone-600 py-3 text-xs uppercase tracking-widest hover:bg-stone-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isResetting}
                  className="flex-1 bg-stone-900 text-white py-3 text-xs uppercase tracking-widest hover:bg-gold-600 transition-colors disabled:opacity-70 flex items-center justify-center gap-2"
                >
                  {isResetting ? (
                    <>
                      <RefreshCw size={14} className="animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Reset Link'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Auth;